import {
  callLength,
  formatPhone,
  recentsLabel,
  type Job,
} from "@roof-hawk/core";
import { useMemo, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { fmtDateTime } from "../../lib/format";
import { navigate } from "../../lib/hooks";
import { jobLink } from "../../lib/jobLink";
import { signVoicemail, transcribeCall, type CallRow } from "../../lib/queries";
import { CallButton } from "./CallButton";

/**
 * Recents and Voicemail, the two call tabs of the phone (ADR 0310, in the
 * bubble since ADR 0321): every call in or out with the missed ones in red,
 * each with its summary, its words and a Call button; or only the
 * voicemails, with a play button. What a call is called is `recentsLabel`
 * in packages/core.
 */
export function Recents({
  calls,
  loading,
  error,
  jobById,
  voicemailOnly,
  line,
  onReread,
  onLeave,
}: {
  calls: CallRow[];
  /** The calls have not been read yet, as against there being none. */
  loading: boolean;
  error: string | null;
  jobById: Map<string, Job>;
  voicemailOnly: boolean;
  /** The line a call back leaves from, as the phone's picker chose it. */
  line: string | null;
  /** Fetch the calls again, after a transcript was asked for. */
  onReread: () => Promise<void>;
  /** Called before a row sends the rep to the job. */
  onLeave: () => void;
}) {
  const rows = useMemo(
    () => (voicemailOnly ? calls.filter((c) => c.voicemail_at) : calls),
    [calls, voicemailOnly],
  );

  return (
    <>
      <ErrorNote error={error} />
      {loading ? <div className="msg-empty">Loading…</div> : null}
      {!loading && !error && rows.length === 0 ? (
        <div className="msg-empty">
          {voicemailOnly ? "No voicemails." : "No calls yet."}
        </div>
      ) : null}
      {rows.length ? (
        <div
          className="list"
          role="list"
          aria-label={voicemailOnly ? "Voicemails" : "Recent calls"}
        >
          {rows.map((call) => (
            <RecentRow
              key={call.call_sid}
              call={call}
              job={call.job_id ? (jobById.get(call.job_id) ?? null) : null}
              line={line}
              onReread={onReread}
              onLeave={onLeave}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

function RecentRow({
  call,
  job,
  line,
  onReread,
  onLeave,
}: {
  call: CallRow;
  job: Job | null;
  line: string | null;
  onReread: () => Promise<void>;
  onLeave: () => void;
}) {
  const { label, missed } = recentsLabel(call);
  const seconds =
    label === "Voicemail" ? call.recording_seconds : call.duration_seconds;
  const number = call.customer_number ? `+1${call.customer_number}` : null;

  return (
    <div className="recent" role="listitem">
      <div className="recent-top">
        <span className={`recent-kind${missed ? " missed" : ""}`}>{label}</span>
        <RecentWho call={call} onLeave={onLeave} />
        <span className="recent-when">
          {fmtDateTime(call.started_at)}
          {seconds ? ` · ${callLength(seconds)}` : ""}
          {call.lead_source ? ` · ${call.lead_source}` : ""}
          {call.placer?.full_name ? ` · ${call.placer.full_name}` : ""}
        </span>
        <span className="recent-acts">
          <CallButton
            number={number}
            leadSource={job?.lead_source ?? call.lead_source}
            jobId={call.job_id}
            contactId={call.contact_id}
            line={line}
            compact
          />
          <PlayButton path={call.recording_path} />
        </span>
      </div>
      {call.summary ? (
        <div className="recent-summary">{call.summary}</div>
      ) : null}
      <RecentWords call={call} onReread={onReread} />
    </div>
  );
}

/** The name, as a link to the job where there is one. */
function RecentWho({ call, onLeave }: { call: CallRow; onLeave: () => void }) {
  const who = call.contact?.full_name ?? formatPhone(call.customer_number);
  const jobId = call.job_id;
  return (
    <span className="recent-who">
      {jobId ? (
        <a
          href={`#${jobLink(jobId)}`}
          onClick={(e) => {
            e.preventDefault();
            onLeave();
            navigate(jobLink(jobId));
          }}
        >
          {who}
        </a>
      ) : (
        <>
          {who}
          {!call.contact_id ? (
            <span className="na-hint"> · new caller</span>
          ) : null}
        </>
      )}
    </span>
  );
}

/** Play the recording without leaving the panel. */
function PlayButton({ path }: { path: string | null }) {
  const [playing, setPlaying] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!path) return null;

  async function play() {
    try {
      setPlaying(await signVoicemail(path ?? ""));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  if (playing)
    return <audio controls autoPlay src={playing} style={{ height: 32 }} />;
  return (
    <>
      <button
        className="addrow ghost"
        type="button"
        onClick={() => void play()}
      >
        Play
      </button>
      <ErrorNote error={error} />
    </>
  );
}

/**
 * The words, folded away until asked for; or, for a recording not yet read,
 * why not and a button to ask again.
 */
function RecentWords({
  call,
  onReread,
}: {
  call: CallRow;
  onReread: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function reread() {
    setBusy(true);
    setError(null);
    try {
      await transcribeCall(call.call_sid);
      await onReread();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (call.transcript_text) {
    return (
      <div className="recent-words">
        <button
          type="button"
          className="linkish"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? "Hide transcript" : "Read transcript"}
        </button>
        {open ? <pre className="convo-text">{call.transcript_text}</pre> : null}
      </div>
    );
  }
  if (!call.recording_path) return null;
  return (
    <div className="recent-words na-hint">
      {call.transcription_error
        ? `Could not be transcribed: ${call.transcription_error} `
        : "Being transcribed… "}
      <button
        type="button"
        className="linkish"
        disabled={busy}
        onClick={() => void reread()}
      >
        {busy ? "Reading…" : "Read it again"}
      </button>
      <ErrorNote error={error} />
    </div>
  );
}
